import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { laseaUserService } from '../../services/laseaUserService';
import { getInventoryStats } from '../../services/laseaInventoryService';


const ClientProfileSidebar = ({ isOpen = false, onClose }) => {
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    accountNumber: '',
    memberSince: '',
    clientImage: '',
    totalShipments: 0,
    activeInventory: 0
  });
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    try {
      setLoading(true);

      // Fetch user profile from Supabase
      const profile = await laseaUserService?.getCurrentUser();

      // Fetch inventory stats
      const stats = await getInventoryStats(profile?.id);

      // Format member since date
      const memberSince = profile?.createdAt
        ? new Date(profile?.createdAt)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : 'N/A';

      setClientData({
        name: profile?.fullName || 'N/A',
        email: profile?.email || 'N/A',
        phone: profile?.phone || 'N/A',
        company: profile?.companyName || 'N/A',
        accountNumber: profile?.id?.substring(0, 13)?.toUpperCase() || 'N/A',
        memberSince: memberSince,
        clientImage: profile?.clientImage || null,
        totalShipments: 0, // This would need a separate shipments table
        activeInventory: stats?.activeItems || 0
      });
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Get current user ID
      const profile = await laseaUserService.getCurrentUser();

      // Upload image
      const publicUrl = await laseaUserService.uploadProfileImage(file, profile?.id);

      // Update user profile with new image URL
      await laseaUserService.updateProfile(profile?.id, { clientImage: publicUrl });

      // Update local state
      setClientData(prev => ({
        ...prev,
        clientImage: publicUrl
      }));

    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-card border-r border-border z-50 transition-page overflow-y-auto ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <h2 className="text-lg font-semibold text-foreground">Profile</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label="Close sidebar"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="flex flex-col items-center mb-6">
            <div
              className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-4 elevation-1 cursor-pointer relative overflow-hidden group"
              onClick={handleImageClick}
              title="Click to change profile photo"
            >
              {clientData.clientImage ? (
                <img
                  src={clientData.clientImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={48} color="var(--color-primary)" />
              )}

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Icon name="Camera" size={24} className="text-white" />
              </div>

              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />

            <h3 className="text-xl font-semibold text-foreground mb-1">
              {clientData?.name}
            </h3>
            <p className="text-sm text-muted-foreground font-mono">
              {clientData?.accountNumber}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Building2" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Company</span>
              </div>
              <p className="text-sm text-foreground ml-9">{clientData?.company}</p>
            </div>

            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Mail" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Email</span>
              </div>
              <p className="text-sm text-foreground ml-9 break-all">{clientData?.email}</p>
            </div>

            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Phone" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Phone</span>
              </div>
              <p className="text-sm text-foreground ml-9">{clientData?.phone}</p>
            </div>

            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Calendar" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Member Since</span>
              </div>
              <p className="text-sm text-foreground ml-9">{clientData?.memberSince}</p>
            </div>
          </div>


        </div>
      </aside>
    </>
  );
};

export default ClientProfileSidebar;